// src/pages/GCodeGenerator.js
import React, {
  useEffect,
  useRef,
  useState,
  useCallback,
  useMemo,
} from "react";
import * as THREE from "three";
import { STLLoader } from "three/examples/jsm/loaders/STLLoader";
import { createCachedMaterialsLaserCutInterlocking } from "../utils/materialManager";
import { initializeThreeScene } from "../utils/initializeThreeScene";
import useVisualHelpers from "../hooks/useVisualHelpers";
import "../styles/common.css";
import TutorialWindow from "../components/TutorialWindow";
import { sliceModelXZ } from "../utils/sliceModel.js"; // 或其他切面方向
import { generateInfillLines } from "../utils/generateInfillLines";
import { createInfillLines } from "../utils/visualizeInfill";
import { STLExporter } from "three/examples/jsm/exporters/STLExporter.js";
import { gcodeGeneratorTutorial } from "../utils/tutorialContents";
import { Polyslice } from "../utils/Polyslice";
import { useCameraSync } from "../contexts/CameraContext";
import { MACHINE_PRESETS } from "../components/Sidebar"; // reuse constants
import { useSyncedCamera } from "../hooks/useSyncedCamera";
import ConstraintWarning from "../components/ConstraintWarning";
// import WarningStack from "../components/WarningStack";
import { checkPrinterFit, onLabConstraintsChange } from "../utils/constraints";
// 通用模板 - 为任何页面添加相机同步


function getCurrentMachineBed(machineType) {
  const settings = window.labConstraints?.machineSettings?.[machineType];
  if (!settings) return null;
  return MACHINE_PRESETS[machineType][settings.selected];
}
// 1. 导入

const GCodeGenerator = ({ stlFile }) => {
  const containerRef = useRef(null);
  const sceneRef = useRef(null);
  const rendererRef = useRef(null);
  const cameraRef = useRef(null);
  const controlsRef = useRef(null);
  const { createVisualization, removeVisualHelpers } = useVisualHelpers();
  const helpersGroupRef = useRef(null);
  const gridHelperRef = useRef(null);
  const polysliceRef = useRef(null);
  const { updateCameraState } = useCameraSync();
  // State variables
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [gcodeReady, setGcodeReady] = useState(false);
  const [modelDimensions, setModelDimensions] = useState(null);
  const [layerHeight, setLayerHeight] = useState(0.2);

  const [infillDensity, setInfillDensity] = useState(5);
  const [nozzleDiameter, setNozzleDiameter] = useState(0.4);
  const infillSpacing = nozzleDiameter * (100 / infillDensity);
  // const [bedWarning, setBedWarning] = useState(null);
  const [warnings, setWarnings] = useState([]);
  const [printSpeed, setPrintSpeed] = useState(60);
  const [bedTemp, setBedTemp] = useState(60);
  const [nozzleTemp, setNozzleTemp] = useState(200);
  const [expanded, setExpanded] = useState(false);
  const [processingStatus, setProcessingStatus] = useState("");
  const [tutorialVisible, setTutorialVisible] = useState(false);
  const [slicingProgress, setSlicingProgress] = useState(0);
  const [previewSlices, setPreviewSlices] = useState([]);
  const [currentLayer, setCurrentLayer] = useState(0);
  const [generatedGcode, setGeneratedGcode] = useState("");
  const [estimatedTime, setEstimatedTime] = useState(null);
  const [panelExpanded, setPanelExpanded] = useState(false);

  // Object references
  const objRef = useRef(null);
  // Heuristics for "not ideal for quick iteration"
  const slowBuildLikely =
  (estimatedTime !== null && estimatedTime > 45 * 60) ||  // > 45 min
  infillDensity > 40 ||                                   // dense infill
  layerHeight < 0.15;                                     // very fine layers


  const pageId = useMemo(() => `gcode-generator`, []);
  useSyncedCamera(pageId, cameraRef, controlsRef);

  // 添加相机同步的补充监听
  // 优化的定时器版本，使用 requestAnimationFrame 替代 setInterval
  useEffect(() => {
    if (!cameraRef.current || !controlsRef.current) return;

    let animationId;
    let lastUpdateTime = 0;
    const updateInterval = 5; // 100ms 更新间隔

    const updateLoop = (currentTime) => {
      if (currentTime - lastUpdateTime >= updateInterval) {
        if (cameraRef.current && controlsRef.current) {
          updateCameraState(pageId, cameraRef.current, controlsRef.current);
        }
        lastUpdateTime = currentTime;
      }
      animationId = requestAnimationFrame(updateLoop);
    };

    animationId = requestAnimationFrame(updateLoop);

    return () => {
      if (animationId) {
        cancelAnimationFrame(animationId);
      }
    };
  }, [pageId, updateCameraState]);
  
  useEffect(() => {
    const off = onLabConstraintsChange(() => {
      if (objRef.current) {
        const w = checkPrinterFit(objRef.current);
        setWarnings(w ? [w] : []);
      }
    });
    return off; // clean up listener on unmount
  }, []);
  
  useEffect(() => {
    const handler = () => {
      if (!objRef.current) return;
      // re-run your size check and update warnings
      const bedDims = getCurrentMachineBed("printer3D"); // or your shared helper
      const box = new THREE.Box3().setFromObject(objRef.current);
      const sz = box.getSize(new THREE.Vector3());
      const tooBig =
        sz.x > bedDims.width || sz.y > bedDims.height || sz.z > bedDims.depth;
      setWarnings(
        tooBig
          ? [{
              type: "size_exceeded",
              part: "Imported Model",
              printer: window.labConstraints?.machineSettings?.printer3D?.selected || "Default",
              currentSize: { width: +sz.x.toFixed(1), height: +sz.y.toFixed(1), depth: +sz.z.toFixed(1) },
              maxSize: bedDims,
            }]
          : []
      );
    };
    window.addEventListener("labConstraints:updated", handler);
    return () => window.removeEventListener("labConstraints:updated", handler);
  }, []);
  

  // Cached materials for reuse
  const cachedMaterials = useMemo(
    () => createCachedMaterialsLaserCutInterlocking(),
    []
  );

  const formatTime = (totalSeconds) => {
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = Math.floor(totalSeconds % 60);
    return `${minutes}m ${seconds}s`;
  };

  // 初始化Polyslice
  useEffect(() => {
    polysliceRef.current = new Polyslice({
      autohome: true,
      nozzleTemperature: nozzleTemp,
      bedTemperature: bedTemp,
      fanSpeed: 100,
    });
  }, [bedTemp, nozzleTemp]);

  useEffect(() => {
    setGcodeReady(false);
    setEstimatedTime(null);
  }, [layerHeight, infillDensity, nozzleDiameter, printSpeed]);

  const toggleTutorial = useCallback(() => {
    setTutorialVisible((prev) => !prev);
  }, []);

  const handleDownloadSTLAndShowTutorial = useCallback(() => {
    if (!objRef.current) return;

    const exporter = new STLExporter();
    const stlString = exporter.parse(objRef.current); // 默认文本格式
    const blob = new Blob([stlString], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "imported_model.stl";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);

    setTutorialVisible(true); // 打开教程面板
  }, []);

  // Initialize grid and axes helpers
  const initHelpers = useCallback(() => {
    if (!sceneRef.current) return;

    if (!gridHelperRef.current) {
      const gridHelper = new THREE.GridHelper(200, 20, 0x888888, 0xaaaaaa);
      gridHelper.position.y = -0.5;
      gridHelper.material.opacity = 0.5;
      gridHelper.material.transparent = true;
      sceneRef.current.add(gridHelper);
      gridHelperRef.current = gridHelper;

      const axesHelper = new THREE.AxesHelper(100);
      sceneRef.current.add(axesHelper);

      console.log("Grid and axes helpers initialized");
    }
  }, []);

  // Clear previous preview slices
  const clearPreviousObjects = useCallback(() => {
    const scene = sceneRef.current;
    if (!scene) return;

    // Preserve the grid helper while clearing previous objects
    if (gridHelperRef.current) {
      scene.remove(gridHelperRef.current);
      gridHelperRef.current = null;
    }

    if (helpersGroupRef.current) {
      removeVisualHelpers(scene);
      helpersGroupRef.current = null;
    }

    // Remove preview slices
    previewSlices.forEach((slice) => {
      scene.remove(slice);
      if (slice.geometry) slice.geometry.dispose();
      if (slice.material) {
        if (Array.isArray(slice.material)) {
          slice.material.forEach((m) => m.dispose());
        } else {
          slice.material.dispose();
        }
      }
    });
    setPreviewSlices([]);

    // Show the original model
    if (objRef.current) {
      objRef.current.visible = false;
    }
  }, [previewSlices, removeVisualHelpers]);

  // Create visual helpers
  const createHelpers = useCallback(() => {
    if (!sceneRef.current || !objRef.current) return;

    // Remove existing helpers
    if (helpersGroupRef.current) {
      removeVisualHelpers(sceneRef.current);
      helpersGroupRef.current = null;
    }

    // Calculate bounding box from the original model
    const boundingBox = new THREE.Box3().setFromObject(objRef.current);

    // Create helpers based on the bounding box
    helpersGroupRef.current = createVisualization(
      objRef.current,
      sceneRef.current,
      boundingBox
    );
  }, [createVisualization, removeVisualHelpers]);

  // 生成G-code
  const generateGcode = useCallback(
    async ({ autoDownload = true, showTutorial = true } = {}) => {
      if (!objRef.current || !polysliceRef.current) return;

      setProcessingStatus("Generating G-code...");

      // 设置切片参数
      const polyslice = polysliceRef.current;
      polyslice.setNozzleTemperature(nozzleTemp);
      polyslice.setBedTemperature(bedTemp);

      // 创建切片选项
      const options = {
        layerHeight: layerHeight,
        infillDensity: infillDensity,
        nozzleDiameter: nozzleDiameter,
        printSpeed: printSpeed,
        travelSpeed: printSpeed * 2,
        bedTemperature: bedTemp,
        nozzleTemperature: nozzleTemp,
        fanSpeed: 100,
      };

      // 生成G-code
      const { gcode, estimatedTime } = await polyslice.slice(
        objRef.current,
        options
      );
      setGeneratedGcode(gcode);
      setEstimatedTime(estimatedTime);

      if (autoDownload) {
        const blob = new Blob([gcode], { type: "text/plain" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = "CAMeleon_print.gcode";
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      }

      if (showTutorial) {
        setTutorialVisible(true);
      }

      setProcessingStatus("");
    },
    [
      bedTemp,
      infillDensity,
      layerHeight,
      nozzleDiameter,
      nozzleTemp,
      printSpeed,
    ]
  );

  // 使用Polyslice切片模型
  const sliceModel = useCallback(() => {
    if (!objRef.current || !sceneRef.current || !polysliceRef.current) return;

    setProcessingStatus("Slicing model...");
    setSlicingProgress(0);

    // 清除之前的对象
    clearPreviousObjects();

    const obj = objRef.current;
    const scene = sceneRef.current;

    // 隐藏原始模型，预览切片
    obj.visible = false;

    // 计算模型尺寸
    const boundingBox = new THREE.Box3().setFromObject(obj);
    const size = boundingBox.getSize(new THREE.Vector3());

    // ⚠️ 把计算放这里，确保最新值被用
    const spacing = nozzleDiameter * (100 / infillDensity);

    // 确保网格辅助工具可见
    // initHelpers();

    // 计算层数
    const totalLayers = Math.ceil(size.y / layerHeight);
    const newSlices = [];

    // 创建切片预览
    const createSlices = async (layerIndex = 0) => {
      if (layerIndex >= totalLayers) {
        setProcessingStatus("");
        setGcodeReady(true);
        setSlicingProgress(100);
        return;
      }

      setSlicingProgress(Math.floor((layerIndex / totalLayers) * 100));

      const sliceHeight = boundingBox.min.y + layerIndex * layerHeight;

      const result = await sliceModelXZ(obj, scene, sliceHeight);
      if (result && result.outlines) {
        result.outlines.forEach((line) => {
          scene.add(line);
          newSlices.push(line);
        });
      }

      if (result.contourPoints.length > 0) {
        for (const polyline of result.contourPoints) {
          if (polyline.length < 3) continue;

          const polygon2D = polyline.map(([x, , z]) => [x, z]);
          const infillSegments = generateInfillLines(polygon2D, infillSpacing);

          const infillLines = createInfillLines(infillSegments, sliceHeight);
          scene.add(infillLines);
          newSlices.push(infillLines); // 确保能被清除
        }
      }

      setTimeout(() => {
        createSlices(layerIndex + 1);
      }, 50);
    };

    // 开始创建切片
    createSlices(0);
    setPreviewSlices(newSlices);

    // 设置当前层为0
    setCurrentLayer(0);
    generateGcode({ autoDownload: false, showTutorial: false });
  }, [
    clearPreviousObjects,
    initHelpers,
    layerHeight,
    infillDensity,
    generateGcode,
  ]);

  // 显示特定层
  const showLayer = useCallback(
    (layerIndex) => {
      if (previewSlices.length === 0) return;

      const totalLayers = previewSlices.length;
      if (layerIndex < 0) layerIndex = 0;
      if (layerIndex >= totalLayers) layerIndex = totalLayers - 1;

      // 设置所有层的可见性
      previewSlices.forEach((slice, index) => {
        slice.visible = index <= layerIndex;
      });

      setCurrentLayer(layerIndex);
    },
    [previewSlices]
  );

  // 加载模型
  const loadModel = useCallback(() => {
    setIsLoading(true);
    setError(null);
    setGcodeReady(false);

    // 清理已存在的模型
    if (objRef.current) {
      sceneRef.current?.remove(objRef.current);
      objRef.current.geometry?.dispose();
      if (Array.isArray(objRef.current.material)) {
        objRef.current.material.forEach((m) => m.dispose());
      } else {
        objRef.current.material?.dispose();
      }
      objRef.current = null;
    }

    initHelpers();

    const loader = new STLLoader();

    const modelPath = stlFile || "/models/twistT.stl";
    console.log("Loading model from:", modelPath);

    loader.load(
      modelPath,
      (geometry) => {
        const material = cachedMaterials.originalModelMaterial;
        const mesh = new THREE.Mesh(geometry, material);

        geometry.computeBoundingBox();
        const boundingBox = geometry.boundingBox;
        const center = new THREE.Vector3();
        boundingBox.getCenter(center);

        // 正确定位模型
        geometry.translate(-center.x, -center.y, -center.z);
        geometry.rotateX(-Math.PI / 2);
        geometry.rotateY(-Math.PI / 2);

        mesh.updateMatrix();
        mesh.updateMatrixWorld(true);

        // 设置位置
        const objectBoundingBox = new THREE.Box3().setFromObject(mesh);
        mesh.position.y -= objectBoundingBox.min.y;
        mesh.updateMatrix();
        mesh.updateMatrixWorld(true);

        mesh.castShadow = true;
        mesh.receiveShadow = true;

        // 保存模型尺寸
        const size = new THREE.Vector3();
        objectBoundingBox.getSize(size);
        setModelDimensions({
          width: size.x,
          height: size.y,
          depth: size.z,
        });
        
        // console.log("📐 Model dimensions:", {
        //   width: size.x.toFixed(2),
        //   height: size.y.toFixed(2),
        //   depth: size.z.toFixed(2),
        // });
        
        // // Also log selected machine info
        // console.log("🖨️ Current printer selection:",
        //   window.labConstraints?.machineSettings?.printer3D
        // );
  
        // ======= Bed Size Check =======
        // const bedDims = getCurrentMachineBed("printer3D"); 
        // if (size.x > bedDims.width || size.y > bedDims.height || size.z > bedDims.depth) {
        //   setBedWarning({
        //     model: {
        //       x: size.x.toFixed(1),
        //       y: size.y.toFixed(1),
        //       z: size.z.toFixed(1),
        //     },
        //     bed: bedDims,
        //     message: "Model size exceeds bed size — cannot manufacture.",
        //   });
        //   setError("Model size exceeds bed size — cannot manufacture.");
        //   setIsLoading(false);
        //   return; // 直接中止，不再继续添加到场景或生成可视化
        // }
        // ==============================

        objRef.current = mesh;
        //sceneRef.current?.add(mesh);
        const w = checkPrinterFit(mesh);
        setWarnings(w ? [w] : []);
        setIsLoading(false);

        // 创建可视化帮助
        createHelpers();
      },
      (xhr) => {
        const percentComplete = (xhr.loaded / xhr.total) * 100;

        if (percentComplete < 100) {
          setProcessingStatus(`Loading model: ${Math.round(percentComplete)}%`);
        } else {
          setProcessingStatus(""); // 加这一句，清掉提示
        }
      },

      (error) => {
        console.error(`Error loading STL from ${modelPath}:`, error);
        setError(
          "Failed to load STL model. Please check the file path or try another file."
        );
        setIsLoading(false);
      }
    );
  }, [stlFile, cachedMaterials, initHelpers, createHelpers]);

  const applyPresetPLA = useCallback(() => {
    setNozzleTemp(200);
    setBedTemp(60);
    setPrintSpeed(60);
  }, []);

  const applyPresetTPU = useCallback(() => {
    setNozzleTemp(230);
    setBedTemp(50);
    setPrintSpeed(30);
  }, []);

  // 初始化Three.js场景
  useEffect(() => {
    const cleanup = initializeThreeScene(
      containerRef,
      sceneRef,
      rendererRef,
      cameraRef,
      controlsRef
    );

    // 场景初始化后加载模型
    setTimeout(() => {
      loadModel();
    }, 0);

    return () => {
      // 卸载前清理可视化帮助
      if (sceneRef.current) {
        removeVisualHelpers(sceneRef.current);
      }

      cleanup();
    };
  }, [loadModel, removeVisualHelpers]);

  // stlFile变化时重新加载模型
  useEffect(() => {
    if (stlFile && sceneRef.current) {
      loadModel();
    }
  }, [stlFile, loadModel]);

  return (
    <div className="container">
      {/* 🔔 Global warning overlay */}
      {warnings.length > 0 && (
        <div style={{ position: "fixed", top: 16, left: "50%", transform: "translateX(-50%)", zIndex: 1000 }}>
          {warnings.map((w, i) => (
            <ConstraintWarning
              key={i}
              warning={w}
              onClose={() => setWarnings(prev => prev.filter((_, idx) => idx !== i))}
            />
          ))}
        </div>
      )}
      <button
        className="panel-toggle-button"
        onClick={() => setPanelExpanded(!panelExpanded)}
      >
        {panelExpanded ? "⟨ Hide Panel" : "⟩ Show Panel"}
      </button>

      <div ref={containerRef} className="canvas-container"></div>

      {/* 浮动控制面板 */}
      {!isLoading && panelExpanded && (
        <div className="controls-panel">
          <div className="panel-title">
  3D Print
  {slowBuildLikely && (
    <span className="badge badge-warn" title="High print time or dense settings">
      Not Ideal for Quick Iteration
    </span>
  )}
</div>

 {/* 基本说明段落 */}
          <div className="panel-section">
            <div className="section-header">Overview</div>
            <div className="section-content">
              <p className="section-description">
                Instead of exporting STL files to external slicers, this
                simulator allows you to preview slicing results directly
                in-browser. It supports basic parameter tuning (e.g., infill,
                layer height) and provides approximate print time estimates. Two
                material presets—PLA and TPU—are available.
              </p>
            </div>
          </div>
          {/* 导出和教程按钮 */}
          <div className="panel-section">
            <div className="section-header">Export & Guidence</div>

            <div className="section-content">
              <button
                onClick={sliceModel}
                className="action-button"
                disabled={isLoading || !objRef.current}
              >
                Slice Model
              </button>
              <button
                onClick={handleDownloadSTLAndShowTutorial}
                className="action-button"
                disabled={isLoading || !objRef.current}
              >
                Download STL
              </button>

              <button onClick={toggleTutorial} className="action-button">
                {tutorialVisible ? "Hide Tutorial" : "Show guidence"}
              </button>
              {estimatedTime !== null && (
                <div className="estimated-time-box">
                  Estimated Printing Time: {formatTime(estimatedTime)}
                </div>
              )}
            </div>
          </div>

         

          {/* Time & Suitability */}
<div className="panel-section">
  <div className="section-header">Time & Suitability</div>
  <div className="section-content">
    <ul className="tip-list">
      <li>
        <strong>Best for:</strong> quick param tuning (layer height, infill),
        sanity-checking slicing, teaching demos, and generating approximate print time.
      </li>
      <li>
        <strong>Not ideal when:</strong> you’re on a tight deadline, models are very large,
        filament is limited, or mechanical strength needs careful path planning.
      </li>
      <li>
        <strong>Iteration speed tip:</strong> use higher layer height (≥ 0.24 mm) and lower infill (≤ 15%)
        to cut time dramatically during early exploration.
      </li>
      <li>
        <strong>Switch to a full slicer</strong> (PrusaSlicer/Cura/etc.) for advanced features:
        supports, variable layer height, per-region settings, bridging control.
      </li>
    </ul>

    {estimatedTime !== null && (
      <div className="note">
        Estimated time: <strong>{formatTime(estimatedTime)}</strong>
        {slowBuildLikely && (
          <> — consider coarser layers or lower infill for faster iteration.</>
        )}
      </div>
    )}

    {/* {bedWarning && (
      <div style={{ position: "fixed", top: 16, left: "50%", transform: "translateX(-50%)", zIndex: 1000 }}>
        <ConstraintWarning
          warning={{
            part: "Imported Model",
            printer: window.labConstraints?.machineSettings?.printer3D?.selected || "Default",
            currentSize: bedWarning.model,
            maxSize: bedWarning.bed,
          }}
          onClose={() => setBedWarning(null)}
        />
      </div>
    )} */}
  </div>
</div>


          {/* 材料预设 */}
          <div className="panel-section">
            <div className="section-header">Material Presets</div>
            <div className="section-content">
              <p className="section-description">
                Apply recommended temperatures and speeds for common filaments.
              </p>
              <button className="sub-button" onClick={applyPresetPLA}>
                PLA Preset
              </button>
              <button className="sub-button" onClick={applyPresetTPU}>
                TPU Preset
              </button>
            </div>
          </div>

          {/* 参数控制 */}
          <div className="panel-section">
            <div className="section-header">Print Parameters</div>
            <div className="section-content">
              <div className="input-group">
                <label>Layer Height (mm)</label>
                <div className="value-display">
                  <input
                    type="range"
                    className="slider"
                    min="0.1"
                    max="0.4"
                    step="0.05"
                    value={layerHeight}
                    onChange={(e) => setLayerHeight(parseFloat(e.target.value))}
                    disabled={isLoading}
                  />
                  <div className="value">{layerHeight}</div>
                </div>
              </div>

              <div className="input-group">
                <label>Infill Density (%)</label>
                <div className="value-display">
                  <input
                    type="range"
                    className="slider"
                    min="0"
                    max="100"
                    step="5"
                    value={infillDensity}
                    onChange={(e) => setInfillDensity(parseInt(e.target.value))}
                    disabled={isLoading}
                  />
                  <div className="value">{infillDensity}</div>
                </div>
              </div>
            </div>
          </div>

          {/* 层控制 */}
          {previewSlices.length > 0 && (
            <div className="panel-section">
              <div className="section-header">Layer Preview</div>
              <div className="section-content">
                <label>
                  Current Layer: {currentLayer + 1} / {previewSlices.length}
                </label>
                <div className="value-display">
                  <input
                    type="range"
                    className="slider"
                    min="0"
                    max={previewSlices.length - 1}
                    value={currentLayer}
                    onChange={(e) => showLayer(parseInt(e.target.value))}
                  />
                  <div className="value">{currentLayer + 1}</div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* 加载信息 */}
      {isLoading && <div className="loading-box">Loading model...</div>}

      {/* 处理状态 */}
      {processingStatus && (
        <div className="loading-box">
          {processingStatus}
          {slicingProgress > 0 && slicingProgress < 100 && (
            <div className="progress-bar-container">
              <div
                className="progress-bar"
                style={{ width: `${slicingProgress}%` }}
              ></div>
              <span className="progress-text">{slicingProgress}%</span>
            </div>
          )}
        </div>
      )}

      {/* 错误信息 */}
      {error && <div className="error-box">error: {error}</div>}

      <TutorialWindow
        visible={tutorialVisible}
        onToggleVisibility={toggleTutorial}
        tutorialContent={gcodeGeneratorTutorial}
      />
    </div>
  );
};

export default GCodeGenerator;
